import HTMLElementInput from './HTMLElementInput';
import type {
  CreateButtonParams,
  CreateCheckBoxParams,
  CreateActionDialogParams,
  CreateElementParams,
  CreateImageParams,
  CreateInputTextParams,
  CreateLabelParams,
  CreateSelectBoxParams,
  CreateTextAreaParams,
} from './types';

export function createElement(
  elementType: string,
  { elementID, parentElement, style = {} as Partial<CSSStyleDeclaration> }: CreateElementParams,
): HTMLElement {
  const element = document.createElement(elementType);
  if (elementID) element.setAttribute('id', elementID);
  Object.assign(element.style, style);
  if (parentElement) parentElement.appendChild(element);
  return element;
}

export function createDiv(createParams: CreateElementParams): HTMLDivElement {
  return createElement('div', createParams) as HTMLDivElement;
}

export function createSpan(createParams: CreateElementParams): HTMLSpanElement {
  return createElement('span', createParams) as HTMLSpanElement;
}

export function createImage(createParams: CreateImageParams): HTMLImageElement {
  const { src, onClick } = createParams;
  const element = createElement('img', createParams) as HTMLImageElement;
  element.setAttribute('src', src);
  if (onClick) element.onclick = onClick;
  return element;
}

export function createButton(createParams: CreateButtonParams): HTMLButtonElement {
  const { labelText, onClick } = createParams;
  const element = createElement('button', createParams) as HTMLButtonElement;
  element.type = 'button';
  element.innerHTML = labelText;
  element.onclick = onClick;
  return element;
}

export function createLabel(createParams: CreateLabelParams): HTMLLabelElement {
  const { labelText = '' } = createParams;
  const element = createElement('label', createParams) as HTMLLabelElement;
  element.innerHTML = labelText;
  return element;
}

export function createCheckBox(createParams: CreateCheckBoxParams): HTMLInputElement {
  const { onChange } = createParams;
  const element = createElement('input', createParams) as HTMLInputElement;
  element.type = 'checkbox';
  if (onChange)
    element.addEventListener('change', () => {
      onChange(element.checked);
    });
  return element;
}

export function createSelectBox(createParams: CreateSelectBoxParams): HTMLSelectElement {
  const { onChange, options, optionsStyle } = createParams;
  const element = createElement('select', createParams) as HTMLSelectElement;

  options.forEach(({ value, text }) => {
    const option = createElement('option', {
      style: optionsStyle,
    }) as HTMLOptionElement;
    option.value = value;
    option.text = text;
    element.add(option);
  });

  if (onChange)
    element.addEventListener('change', () => {
      onChange(element.value, element.selectedIndex);
    });
  return element;
}

export function createInputText(createParams: CreateInputTextParams): HTMLInputElement {
  const element = createElement('input', createParams) as HTMLInputElement;
  const { initialValue = '', onEnterKey, restrictRegEx, editColor } = createParams;
  HTMLElementInput.setTextInputEvents(element, onEnterKey, restrictRegEx, editColor);
  element.value = initialValue;
  return element;
}

export function createTextArea(createParams: CreateTextAreaParams): HTMLTextAreaElement {
  const textArea = createElement('textarea', createParams) as HTMLTextAreaElement;
  textArea.cols = createParams.cols ?? 40;
  textArea.rows = createParams.rows ?? 40;
  textArea.autocomplete = createParams.autocomplete ?? 'off';
  textArea.autocapitalize = createParams.autocapitalize ?? 'off';
  textArea.spellcheck = createParams.spellcheck ?? false;
  return textArea;
}

export function createActionDialog(createParams: CreateActionDialogParams): HTMLDivElement {
  const div = createDiv({
    elementID: createParams.elementID,
    parentElement: document.body,
    style: {
      position: 'fixed',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%,-50%)',
      zIndex: createParams.zIndex?.toString?.(),
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(1,1,1,0.4)',
    },
  });

  const dialog = createDiv({
    parentElement: div,
    style: {
      position: 'fixed',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%,-50%)',
      textAlign: 'center',
      width: '300px',
      padding: '10px 10px 10px 10px',
      borderRadius: '10px',
      backgroundColor: '#aaaaaa',
      ...createParams.dialogStyle,
    },
  });

  const title = createSpan({ parentElement: dialog });
  title.innerHTML = `<h2>${createParams.titleText ?? 'Confirm Action?'}<h2>`;

  const buttonStyle: Partial<CSSStyleDeclaration> = {
    fontSize: '1.2em',
    borderRadius: '5px',
    minHeight: '25px',
    minWidth: '100px',
    margin: '5px 5px 5px 5px',
    ...createParams.buttonStyle,
  };

  if (createParams.showCancel !== false) {
    createButton({
      style: buttonStyle,
      parentElement: dialog,
      labelText: createParams.cancelText ?? 'cancel',
      onClick: () => {
        div.remove();
        createParams.onCancel?.();
      },
    });
  }

  if (createParams.showConfirm !== false) {
    createButton({
      style: buttonStyle,
      parentElement: dialog,
      labelText: createParams.confirmText ?? 'confirm',
      onClick: () => {
        div.remove();
        createParams.onConfirm?.();
      },
    });
  }

  return div;
}
